import type { AdhdVariant } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { adhdCopy } from "../../lib/i18n/adhd";

// ADHDChooser — the A/B/C segmented control (design handoff §"Reusable
// atoms"). Palette-override props let variant B re-tint it per traffic-light
// state so the cream/ink control does not clash on a dark background.
type Props = {
  value: AdhdVariant;
  onChange: (v: AdhdVariant) => void;
  // Palette overrides (variant B states). Default to the global tokens.
  inkColor?: string;
  mutedColor?: string;
  bgColor?: string;
};

// Variant C (Krok) temporarily hidden from the UI while we tune the AI
// suggestion pipeline (rate limits + quality). Backend still accepts it,
// so existing users with adhdVariant === "C" persisted just get normalized
// to "A" at render time in HomeMaybeADHD.
const ORDER: AdhdVariant[] = ["A", "B"];

export function AdhdChooser({ value, onChange, inkColor, mutedColor, bgColor }: Props) {
  const { lang } = useLang();
  const t = adhdCopy[lang];
  const labels: Record<AdhdVariant, string> = {
    A: t.variantA,
    B: t.variantB,
    C: t.variantC,
  };
  const arias: Record<AdhdVariant, string> = {
    A: t.variantAriaA,
    B: t.variantAriaB,
    C: t.variantAriaC,
  };
  return (
    // Block flex container (not inline-flex) to avoid baseline-alignment
    // quirks that caused the buttons' click area to extend above the
    // visible bezel — the user could click only the very top edge of each
    // button. Buttons themselves are oversized (44 px min target) so the
    // hit zone matches the visual bezel exactly.
    <div
      role="group"
      aria-label={t.chooserAria}
      style={{
        display: "flex",
        alignSelf: "flex-start",
        padding: 3,
        borderRadius: 99,
        background: bgColor ?? "var(--surface-2)",
        boxShadow: "0 0 0 1px var(--nav-border)",
        // Force its own stacking context so no later sibling can paint over
        // the bezel and steal clicks.
        position: "relative",
        zIndex: 5,
      }}
    >
      {ORDER.map((v) => {
        const active = v === value;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            aria-pressed={active}
            aria-label={arias[v]}
            className="uppercase transition-colors"
            style={{
              minHeight: 32,
              padding: "9px 14px",
              borderRadius: 99,
              border: "none",
              background: active ? (inkColor ?? "var(--ink)") : "transparent",
              color: active ? "var(--bg)" : (mutedColor ?? "var(--ink-2)"),
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              lineHeight: 1,
              touchAction: "manipulation",
              userSelect: "none",
              WebkitUserSelect: "none",
              cursor: "pointer",
              pointerEvents: "auto",
            }}
          >
            {labels[v]}
          </button>
        );
      })}
    </div>
  );
}
